import theme from 'common/theme'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

const TickerContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const TickerWrapper = styled.div`
  display: flex;
  width: 100%;
  position: relative;
`

const TickerContentWrapper = styled.div`
  overflow: hidden;
  width: 100%;
  height: 100%;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border: ${({ border }) => border};
  border-radius: 10px;
  padding-bottom: ${({ moreThanOneAlert }) =>
    moreThanOneAlert && '12px'};
`

const TickerContent = styled.div`
  display: flex;
  transition: all 250ms linear;
  -ms-overflow-style: none; /* hide scrollbar in IE and Edge */
  scrollbar-width: none; /* hide scrollbar in Firefox */
  transform: translateX(-${(props) => props.activeSlide * 100}%);

  span {
    color: ${theme.colors.redOrange};
    font-weight: bold;
  }

  > * {
    width: fit-content;
    flex-shrink: 0;
    flex-grow: 1;
  }
  &::-webkit-scrollbar,
  &::-webkit-scrollbar {
    display: none;
  }
`

const TickerIndicators = styled.div`
  display: flex;
  justify-content: center;
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  margin-bottom: 16px;
`

const Dot = styled.div`
  display: inline-block;
  cursor: pointer;
  height: 8px;
  width: 8px;
  margin: 0 4px;
  background-color: ${theme.colors.grayLight};
  border-radius: 50%;
  transition: background-color 0.6s ease;
  &:first-child {
    margin-left: 0;
  }
  &.active {
    background-color: ${theme.colors.redOrange};
    border-radius: 4px;
    transition: width 0.5s;
  }
`

interface ITicker {
  activeSlide?: number
  backgroundColor?: string
  border?: string
  interval?: number
  slides: JSX.Element[]
}

function Ticker({
  activeSlide: defaultActiveSlide = 0,
  backgroundColor = theme.colors.orangeYellowLight,
  border = `1px solid ${theme.colors.redOrange}`,
  interval = 5000,
  slides,
  ...otherProps
}: ITicker): JSX.Element {
  const [activeSlide, setActiveSlide] = useState<number>(
    defaultActiveSlide,
  )

  const showSlides = useCallback(
    (index) => {
      if (index >= slides.length) return setActiveSlide(0)
      return setActiveSlide(index)
    },
    [slides.length],
  )

  const goToSlide = useCallback((e) => {
    const slideIndex = e.currentTarget.getAttribute(
      'data-slide-index',
    )
    setActiveSlide(Number(slideIndex))
  }, [])

  useEffect(() => {
    const intervalSlide = setInterval(() => {
      showSlides(activeSlide + 1)
    }, interval)
    return () => clearInterval(intervalSlide)
  }, [activeSlide, interval, showSlides])

  useEffect(() => {
    setActiveSlide(defaultActiveSlide)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides])

  return (
    <TickerContainer data-testid="ticker-container" {...otherProps}>
      <TickerWrapper>
        <TickerContentWrapper
          backgroundColor={backgroundColor}
          border={border}
          moreThanOneAlert={slides.length > 1}
        >
          <TickerContent activeSlide={activeSlide}>
            {slides}
          </TickerContent>
        </TickerContentWrapper>

        {slides.length > 1 ? (
          <TickerIndicators>
            {slides.map((slide, slideIndex) => {
              return (
                <Dot
                  key={String(`dot-${slideIndex}`)}
                  className={
                    activeSlide === slideIndex ? 'active' : ''
                  }
                  onClick={goToSlide}
                  data-slide-index={slideIndex}
                  data-testid="ticker-indicator"
                />
              )
            })}
          </TickerIndicators>
        ) : null}
      </TickerWrapper>
    </TickerContainer>
  )
}

export default Ticker
